// channel partner profile page

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Stack,
  Button,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../../services/api';
import { useSnackbar } from 'notistack';
import { useChannelPartner } from '../../contexts/ChannelPartnerContext';
import { getInitials, formatPhoneNumber } from '../../utils/helper';

const ChannelPartnerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { fetchChannelPartnerById, loading: contextLoading } = useChannelPartner();

  const [loading, setLoading] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [channelPartner, setChannelPartner] = useState(null);
  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLeads, setTotalLeads] = useState(0);
  const [sortField, setSortField] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeTab, setActiveTab] = useState(0);

  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [commissionRateDraft, setCommissionRateDraft] = useState('');
  const [commissionSaving, setCommissionSaving] = useState(false);

  const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
  const [remarksDraft, setRemarksDraft] = useState('');
  const [remarksSaving, setRemarksSaving] = useState(false);

  // Refs to prevent duplicate API calls
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const lastFetchIdRef = useRef(null);
  const leadsFetchedRef = useRef(false);

  // Fetch leads separately - only if not in channel partner response
  const fetchLeadsData = useCallback(async () => {
    if (!id || isFetchingRef.current || leadsFetchedRef.current) {
      return;
    }

    leadsFetchedRef.current = true;

    try {
      setLeadsLoading(true);
      const requestData = {
        channel_partners: [parseInt(id)],
        page: page + 1,
        page_size: rowsPerPage,
        is_deleted: false
      };

      const response = await api.post('/lms/leads/v2/', requestData);
      if (response.data) {
        setLeads(response.data.results || []);
        setTotalLeads(response.data.count || 0);
      } else {
        throw new Error('Failed to fetch leads');
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
      enqueueSnackbar('Error loading leads', { variant: 'error' });
      leadsFetchedRef.current = false; // Reset on error so it can retry
    } finally {
      setLeadsLoading(false);
    }
  }, [id, page, rowsPerPage, enqueueSnackbar]);

  // Fetch channel partner details using context - memoized to prevent re-creation
  const fetchChannelPartnerData = useCallback(async () => {
    if (!id || hasFetchedRef.current || isFetchingRef.current) {
      return;
    }

    // Prevent duplicate calls for the same ID
    if (lastFetchIdRef.current === id) {
      return;
    }

    isFetchingRef.current = true;
    lastFetchIdRef.current = id;

    try {
      setLoading(true);
      setError(null);
      const data = await fetchChannelPartnerById(id);
      
      if (!data) {
        // If fetch was skipped due to duplicate call prevention, return early
        isFetchingRef.current = false;
        return;
      }

      setChannelPartner(data);
      
      // If leads are included in the response, use them
      if (data.leads && Array.isArray(data.leads) && data.leads.length > 0) {
        setLeads(data.leads);
        setTotalLeads(data.leads.length);
        setLeadsLoading(false);
        hasFetchedRef.current = true;
        leadsFetchedRef.current = true; // Mark as fetched so we don't fetch again
      } else {
        // Only fetch leads separately if not included in response
        hasFetchedRef.current = true;
        fetchLeadsData();
      }
    } catch (err) {
      console.error('Error fetching channel partner:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load channel partner details');
      enqueueSnackbar('Error loading channel partner details', { variant: 'error' });
      hasFetchedRef.current = false; // Reset on error
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [id, fetchChannelPartnerById, enqueueSnackbar, fetchLeadsData]);

  // Main effect - fetch channel partner data only once on mount
  useEffect(() => {
    if (id && !hasFetchedRef.current) {
      fetchChannelPartnerData();
    }

    // Cleanup on unmount
    return () => {
      hasFetchedRef.current = false;
      isFetchingRef.current = false;
    };
  }, [id, fetchChannelPartnerData]);

  // Separate effect for pagination - only fetch if leads weren't in initial response
  useEffect(() => {
    // Only fetch if we need pagination and leads weren't in the initial response
    // Reset leads fetched flag when page/rowsPerPage changes for pagination
    if (id && hasFetchedRef.current && channelPartner && (!channelPartner.leads || channelPartner.leads.length === 0)) {
      if (page === 0 && rowsPerPage === 10) {
        // Initial load - already handled in fetchChannelPartnerData
        return;
      }
      // Pagination change - allow refetch
      leadsFetchedRef.current = false;
      fetchLeadsData();
    }
  }, [page, rowsPerPage, fetchLeadsData, channelPartner, id]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  const getStageColor = (stage) => {
    const colors = {
      new: 'default',
      contacted: 'info',
      qualified: 'primary',
      negotiation: 'warning',
      closure: 'secondary',
      junk: 'error'
    };
    return colors[stage] || 'default';
  };

  const getStatusColorForLead = (status) => {
    const colors = {
      assigned: 'info',
      closed_won: 'success',
      lost: 'error',
      not_interested: 'default',
      not_looking_currently: 'default',
      cold_lead: 'default'
    };
    return colors[status] || 'default';
  };

  const openCommissionDialog = () => {
    setCommissionRateDraft(
      channelPartner?.commission_rate !== undefined && channelPartner?.commission_rate !== null
        ? String(channelPartner.commission_rate)
        : ''
    );
    setCommissionDialogOpen(true);
  };

  const openRemarksDialog = () => {
    setRemarksDraft(channelPartner?.remarks ?? '');
    setRemarksDialogOpen(true);
  };

  const handleSaveCommission = async () => {
    if (!id) return;

    const draft = commissionRateDraft === '' ? null : Number(commissionRateDraft);
    if (draft !== null && (Number.isNaN(draft) || draft < 0 || draft > 100)) {
      enqueueSnackbar('Commission rate must be a number between 0 and 100', { variant: 'warning' });
      return;
    }

    try {
      setCommissionSaving(true);
      const response = await api.put(`/lms/channel-partners/${id}/`, { commission_rate: draft });
      const updated = response.data?.data || response.data;
      setChannelPartner(updated);
      setCommissionDialogOpen(false);
      enqueueSnackbar('Commission rate saved', { variant: 'success' });
    } catch (error) {
      console.error('Error saving commission rate:', error);
      enqueueSnackbar(error?.response?.data?.error || error.message || 'Failed to save commission rate', { variant: 'error' });
    } finally {
      setCommissionSaving(false);
    }
  };

  const handleSaveRemarks = async () => {
    if (!id) return;

    try {
      setRemarksSaving(true);
      const remarksPayload = remarksDraft ? remarksDraft.trim() : null;
      const response = await api.put(`/lms/channel-partners/${id}/`, { remarks: remarksPayload });
      const updated = response.data?.data || response.data;
      setChannelPartner(updated);
      setRemarksDialogOpen(false);
      enqueueSnackbar('Remarks saved', { variant: 'success' });
    } catch (error) {
      console.error('Error saving remarks:', error);
      enqueueSnackbar(error?.response?.data?.error || error.message || 'Failed to save remarks', { variant: 'error' });
    } finally {
      setRemarksSaving(false);
    }
  };

  const handleLeadClick = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, color: 'text.disabled', opacity: 0.5 }} />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUpwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />;
  };

  // Sort leads based on sortField and sortOrder
  const sortedLeads = [...leads].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'lead_id':
        aValue = a.lead_id || '';
        bValue = b.lead_id || '';
        break;
      case 'name':
        aValue = a.name || a.contact?.name || '';
        bValue = b.name || b.contact?.name || '';
        break;
      case 'email':
        aValue = a.email || a.contact?.email || '';
        bValue = b.email || b.contact?.email || '';
        break;
      case 'mobile':
        aValue = a.mobile || a.contact?.mobile || '';
        bValue = b.mobile || b.contact?.mobile || '';
        break;
      case 'created':
        aValue = a.created || a.created_at || '';
        bValue = b.created || b.created_at || '';
        break;
      case 'stage':
        aValue = a.stage || '';
        bValue = b.stage || '';
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'owner':
        aValue = a.owner || a.agent?.name || '';
        bValue = b.owner || b.agent?.name || '';
        break;
      default:
        return 0;
    }
    
    if (sortField === 'created') {
      // For dates, parse and compare
      const aDate = aValue ? dayjs(aValue) : dayjs(0);
      const bDate = bValue ? dayjs(bValue) : dayjs(0);
      return sortOrder === 'asc' 
        ? aDate.valueOf() - bDate.valueOf()
        : bDate.valueOf() - aDate.valueOf();
    }
    
    // String comparison
    aValue = String(aValue).toLowerCase();
    bValue = String(bValue).toLowerCase();
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !channelPartner) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Channel partner not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/channel-partners')}>
          Back to Channel Partners
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/channel-partners')} sx={{ mr: 1, p: 0.5 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Channel Partner Profile
        </Typography>
      </Box>

      {/* Profile Summary Card */}
      <Card 
        elevation={2} 
        sx={{ 
          mb: 2,
          background: 'linear-gradient(135deg, rgba(0, 112, 80, 0.05) 0%, rgba(0, 112, 80, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Grid container spacing={3} alignItems="flex-start">
            {/* Left Section - Avatar and Name */}
            <Grid item xs={12} sm={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem',
                    boxShadow: 2,
                    border: '3px solid',
                    borderColor: 'background.paper'
                  }}
                >
                  {getInitials(channelPartner.name || 'CP')}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.2 }}>
                    {channelPartner.name || 'Unnamed Partner'}
                  </Typography>
                  {channelPartner.channel_partner_id && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      Channel Partner ID: #{channelPartner.channel_partner_id}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Right Section - Details Grid */}
            <Grid item xs={12} sm={12} md={8}>
              <Grid container spacing={2}>
                {/* Email */}
                {channelPartner.email && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                        Email
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <EmailIcon color="primary" fontSize="small" sx={{ fontSize: '1rem' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.primary', fontWeight: 500 }}>
                          {channelPartner.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Mobile */}
                {channelPartner.phone && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                        Mobile
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <PhoneIcon color="primary" fontSize="small" sx={{ fontSize: '1rem' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.primary', fontWeight: 500 }}>
                          {formatPhoneNumber(channelPartner.country_code, channelPartner.phone)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Created At */}
                {channelPartner.created_at && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                        Created At
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <ScheduleIcon color="primary" fontSize="small" sx={{ fontSize: '1rem' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.primary', fontWeight: 500 }}>
                          {dayjs(channelPartner.created_at).format('MMM DD, YYYY')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Total Leads */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                      Total Leads
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <AssignmentIcon color="primary" fontSize="small" sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.primary', fontWeight: 600 }}>
                        {channelPartner.leads_count || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Commission Rate */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                        Commission Rate
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={openCommissionDialog}
                        startIcon={
                          channelPartner?.commission_rate != null
                            ? <EditIcon sx={{ fontSize: '0.95rem', opacity: 0.7 }} />
                            : null
                        }
                        sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, fontSize: '0.7rem' }}
                      >
                        {channelPartner?.commission_rate != null ? 'Edit' : 'Add'}
                      </Button>
                    </Box>

                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.primary', fontWeight: 600 }}>
                      {channelPartner?.commission_rate != null ? `${channelPartner.commission_rate}%` : 'Not set'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Remarks */}
                <Grid item xs={12} sm={12} md={6}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1 }}
                      >
                        Remarks
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={openRemarksDialog}
                        startIcon={
                          channelPartner?.remarks
                            ? <EditIcon sx={{ fontSize: '0.95rem', opacity: 0.7 }} />
                            : null
                        }
                        sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, fontSize: '0.7rem' }}
                      >
                        {channelPartner?.remarks ? 'Edit' : 'Add'}
                      </Button>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', maxHeight: 48, overflow: 'hidden' }}
                    >
                      {channelPartner?.remarks ? channelPartner.remarks : 'No remarks added yet.'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Commission Dialog */}
      <Dialog open={commissionDialogOpen} onClose={() => setCommissionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add / Edit Commission Rate</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Commission Rate (%)"
            type="number"
            value={commissionRateDraft}
            onChange={(e) => setCommissionRateDraft(e.target.value)}
            placeholder="e.g. 2.5"
            helperText="Optional. Leave empty to clear."
            inputProps={{ min: 0, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)} disabled={commissionSaving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCommission}
            disabled={commissionSaving}
            sx={{ textTransform: 'none', backgroundColor: '#007050', '&:hover': { backgroundColor: '#005a3f' } }}
          >
            {commissionSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remarks Dialog */}
      <Dialog open={remarksDialogOpen} onClose={() => setRemarksDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add / Edit Remarks</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={5}
            value={remarksDraft}
            onChange={(e) => setRemarksDraft(e.target.value)}
            placeholder="Add internal remarks for this channel partner..."
            helperText="Optional. Leave empty to clear."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarksDialogOpen(false)} disabled={remarksSaving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRemarks}
            disabled={remarksSaving}
            sx={{ textTransform: 'none', backgroundColor: '#007050', '&:hover': { backgroundColor: '#005a3f' } }}
          >
            {remarksSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tabs Section */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ 
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48
              }
            }}
          >
            <Tab label="Leads" />
            <Tab label="Notes" />
          </Tabs>
        </Box>
      </Card>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Card elevation={2}>
          <CardContent sx={{ p: 0 }}>
            <Box 
              sx={{ 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(0, 112, 80, 0.03) 0%, rgba(0, 112, 80, 0.01) 100%)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Leads
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {totalLeads} {totalLeads === 1 ? 'lead' : 'leads'} attached to this channel partner
                  </Typography>
                </Box>
                {channelPartner.status && (
                  <Chip
                    label={channelPartner.status === 'active' ? 'Active' : 'Inactive'}
                    color={getStatusColor(channelPartner.status)}
                    size="small"
                    sx={{ height: 24, fontSize: '0.75rem', fontWeight: 500 }}
                  />
                )}
              </Box>
            </Box>

          {leadsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : leads.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Leads Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This channel partner doesn't have any leads attached yet.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSort('lead_id')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Lead ID
                          {renderSortIcon('lead_id')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSort('name')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Name
                          {renderSortIcon('name')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSort('email')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Email
                          {renderSortIcon('email')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSort('mobile')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Mobile
                          {renderSortIcon('mobile')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSort('created')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Created
                          {renderSortIcon('created')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSort('stage')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Stage
                          {renderSortIcon('stage')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSort('status')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Status
                          {renderSortIcon('status')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSort('owner')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Owner
                          {renderSortIcon('owner')}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedLeads.map((lead, index) => {
                      // Handle leads from serializer (simplified structure) vs leads from API (full structure)
                      const leadId = lead.id || `lead-${index}`;
                      const leadIdDisplay = lead.lead_id || 'N/A';
                      const leadName = lead.name || lead.contact?.name || lead.customer?.name || lead.contact?.first_name || 'N/A';
                      const leadEmail = lead.email || lead.contact?.email || lead.customer?.email || '';
                      const leadMobile = lead.mobile || lead.contact?.mobile || lead.customer?.mobile || '';
                      const leadCreated = lead.created || lead.created_at || '';
                      const leadStage = lead.stage || 'N/A';
                      const leadStageVerbose = lead.stage_verbose || lead.stage || 'N/A';
                      const leadStatus = lead.status || 'N/A';
                      const leadStatusVerbose = lead.status_verbose || lead.status || 'N/A';
                      const ownerName = lead.owner || lead.agent?.name || lead.assigned_agent?.name || null;
                      const leadInitials = getInitials(leadName);

                      return (
                        <TableRow
                          key={leadId}
                          hover
                          sx={{ cursor: lead.id ? 'pointer' : 'default' }}
                          onClick={() => lead.id && handleLeadClick(lead.id)}
                        >
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {leadIdDisplay && leadIdDisplay !== 'N/A' ? `#${leadIdDisplay}` : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: 'primary.main',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {leadInitials}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {leadName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {leadEmail || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {leadMobile || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {leadCreated || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={leadStageVerbose}
                              color={getStageColor(leadStage)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={leadStatusVerbose}
                              color={getStatusColorForLead(leadStatus)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {ownerName ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: 'secondary.main',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {getInitials(ownerName)}
                                </Avatar>
                                <Typography variant="body2">{ownerName}</Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Unassigned
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalLeads > rowsPerPage && (
                <TablePagination
                  component="div"
                  count={totalLeads}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {activeTab === 1 && (
        <ChannelPartnerNotes channelPartnerId={id} />
      )}
    </Box>
  );
};

// Channel Partner Notes Component
const ChannelPartnerNotes = ({ channelPartnerId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (channelPartnerId) {
      fetchNotes();
    }
  }, [channelPartnerId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/lms/channel-partners/${channelPartnerId}/notes/`);
      if (response.data) {
        setNotes(response.data);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      enqueueSnackbar('Error loading notes', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      enqueueSnackbar('Please enter a note', { variant: 'warning' });
      return;
    }

    try {
      if (editMode && editingNote) {
        const response = await api.patch(
          `/lms/channel-partners/${channelPartnerId}/notes/${editingNote.id}/`,
          { note: noteText.trim() }
        );
        if (response.data) {
          enqueueSnackbar('Note updated successfully', { variant: 'success' });
          setEditMode(false);
          setEditingNote(null);
          setNoteText('');
          fetchNotes();
        }
      } else {
        const response = await api.post(
          `/lms/channel-partners/${channelPartnerId}/notes/`,
          { note: noteText.trim() }
        );
        if (response.data) {
          enqueueSnackbar('Note added successfully', { variant: 'success' });
          setNoteText('');
          fetchNotes();
        }
      }
    } catch (error) {
      console.error('Error saving note:', error);
      enqueueSnackbar('Error saving note', { variant: 'error' });
    }
  };

  const handleEdit = (note) => {
    setEditMode(true);
    setEditingNote(note);
    setNoteText(note.note);
  };

  const handleDelete = (note) => {
    setNoteToDelete(note);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (noteToDelete) {
      try {
        await api.delete(`/lms/channel-partners/${channelPartnerId}/notes/${noteToDelete.id}/`);
        enqueueSnackbar('Note deleted successfully', { variant: 'success' });
        setDeleteDialogOpen(false);
        setNoteToDelete(null);
        fetchNotes();
      } catch (error) {
        console.error('Error deleting note:', error);
        enqueueSnackbar('Error deleting note', { variant: 'error' });
      }
    }
  };

  if (loading) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={2}>
      <CardContent>
        {/* Add Note Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            {editMode ? 'Edit Note' : 'Add Note'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter your note here..."
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSaveNote}
              disabled={!noteText.trim()}
            >
              {editMode ? 'Update Note' : 'Add Note'}
            </Button>
            {editMode && (
              <Button
                variant="outlined"
                onClick={() => {
                  setEditMode(false);
                  setEditingNote(null);
                  setNoteText('');
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes List */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Notes ({notes.length})
          </Typography>
          {notes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No notes yet. Add your first note above.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {notes.map((note) => (
                <Paper
                  key={note.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {note.created_by?.name || 'System'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {note.note_date} {note.note_time}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(note)}
                        sx={{ mr: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(note)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {note.note}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Note</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this note? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ChannelPartnerProfile;